import { Box, Checkbox, Dialog, Typography } from "@material-ui/core";
import { isEmpty, isNil } from "lodash-es";
import React from "react";
import { useHistory } from "react-router-dom";
import useSWR from "swr";
import { Contract, Property } from "../../api/models";
import Button, { ButtonSize, ButtonType } from "../../components/common/Button";
import Separator from "../../components/common/Separator";
import TextField from "../../components/common/TextField";
import PrivacyLink from "../../components/PrivacyLink";
import TermsLink from "../../components/TermsLink";
import { useMobile } from "../../hooks/mobile";
import ModalHeader from "./ModalHeader";
import PropertyInfoBar from "./PropertyInfoBar";
import { useAlert } from "../../contexts/Alert";
import noAuthApi from "../../api/noauth";
import api from "../../api/instance";
import { Colors } from "../../constants/colors";
import { parseApiError } from "../../helpers/error";

interface Props {
  open: boolean;
  onClose: () => unknown;
  contractId: number | null;
  inviterName: string;
  inviterFirmName?: string | null;
  token: string | null;
  onSignedUp: () => unknown;
  onLogin: () => unknown;
}

export default function SignUpModal({
  open,
  onClose,
  contractId,
  inviterName,
  inviterFirmName,
  token,
  onSignedUp,
  onLogin,
}: Props) {
  const isMobile = useMobile();
  const history = useHistory();

  const { data, mutate: contractMutate } = useSWR(
    !isNil(contractId) ? `/view-contracts/${contractId}/` : null
  );
  const contract = data ? (data as Contract) : null;

  const { data: propertyData } = useSWR(
    contract?.property_id ? `/view-properties/${contract.property_id}/` : null
  );
  const property = propertyData && (propertyData as Property);

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  // const [firmName, setFirmName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [agreeToTerms, setAgreeToTerms] = React.useState(false);

  const [signingUp, setSigningUp] = React.useState(false);

  // React.useEffect(() => {
  //   inviterFirmName && setFirmName(inviterFirmName);
  // }, []);

  const { setErrorMessage } = useAlert();

  return (
    <Dialog
      open={open}
      aria-labelledby="login-modal"
      aria-describedby="login-modal-description"
      fullScreen={isMobile}
    >
      <Box width={isMobile ? "100%" : "425px"}>
        <ModalHeader />
        <PropertyInfoBar property={property} inviterName={inviterName} />

        <Box pl="39px" pr="39px" pt="43px" pb="40px">
          <TextField
            title="Confirm first name"
            value={firstName}
            onChange={(text) => setFirstName(text)}
          />

          <TextField
            title="Confirm last name"
            value={lastName}
            onChange={(text) => setLastName(text)}
          />
          {/* {inviterFirmName && (
          <TextField
            title="Organisation"
            value={firmName}
            onChange={(text) => setFirmName(text)}
            disabled={true}
          />
          )} */}
          <TextField
            title="Provide a password"
            value={password}
            onChange={(text) => setPassword(text)}
            type="password"
          />

          <Box
            display="flex"
            flexDirection="row"
            mt={1.5}
            alignItems="flex-start"
          >
            <Checkbox
              style={{ color: Colors.Grey2 }}
              checked={agreeToTerms}
              onChange={(event) => {
                setAgreeToTerms(event.target.checked);
              }}
            />
            <Typography variant="body2" style={{ marginTop: "8px" }}>
              I agree to the <TermsLink /> (including those relating to privacy
              and communication) and <PrivacyLink />
            </Typography>
          </Box>
        </Box>

        <Separator />
        <Box
          height="74px"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          boxSizing="border-box"
          pl="16px"
          pr="16px"
        >
          <Button
            title="LOG IN"
            disabled={signingUp}
            type={ButtonType.secondary}
            size={ButtonSize.medium}
            width="105px"
            onClick={onLogin}
          />
          <Button
            loading={signingUp}
            title="CONTINUE"
            type={ButtonType.primary}
            size={ButtonSize.medium}
            width="125px"
            onClick={() => {
              if (isEmpty(firstName)) {
                setErrorMessage("Please enter your first name");
                return;
              }

              if (isEmpty(lastName)) {
                setErrorMessage("Please enter your last name");
                return;
              }

              if (isEmpty(password)) {
                setErrorMessage("Please provide a password");
                return;
              }

              if (!agreeToTerms) {
                setErrorMessage("Please agree to terms and conditions");
                return;
              }

              setSigningUp(true);

              noAuthApi
                .post("/user-confirmation/", {
                  token,
                  first_name: firstName,
                  last_name: lastName,
                  password,
                })
                .then((response) => {
                  // console.debug("user-confirmation response: ", response);

                  const token = response.data.token;

                  localStorage.setItem("token", token);

                  api.defaults.headers.common[
                    "Authorization"
                  ] = `Bearer ${token}`;

                  setSigningUp(false);
                  onSignedUp();
                })
                .catch((error) => {
                  setSigningUp(false);

                  setErrorMessage(parseApiError(error));
                });
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
}
